import { RoleName, OclViewItem } from "./types";
import { PermissionAssignment, PermissionAssignmentsData } from "../../redux/types/permissionTypes";
import { ALL_OPTION } from "./constants";
import ROUTE_CONFIG from "../../config/routeConfig";

export const getRoleBadgeColor = (role: RoleName | string): "red" | "blue" | "grey" | "green" => {
  switch (role) {
    case "System Admin": return "red";
    case "Administrator": return "blue";
    case "Update Program": return "green";
    case "Edit User": return "green";
    case "Viewer": return "grey";
    default: return "grey";
  }
};

export const getRoleAbbreviation = (role: string): string => {
  switch (role) {
    case "System Admin": return "S";
    case "Administrator": return "A";
    case "Viewer": return "V";
    case "Update Program": return "U";
    case "Edit User": return "E";
    default: return role.charAt(0);
  }
};

/**
 * Get the display label for a feature.
 * Accepts an API feature name (e.g. "configuration_setting").
 * Looks up ROUTE_CONFIG for the menu label; falls back to the input string if not found.
 */
export function getFeatureLabel(apiFeature: string): string {
  const route = ROUTE_CONFIG.find((r) => r.id === apiFeature);
  if (route) return route.label;
  return apiFeature;
}

/**
 * Build a Map from action ID to display name from constantsData.feature_actions.
 */
export function buildActionIdToNameMap(featureActions: Record<string, Record<string, Array<Record<string, string>>>> | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!featureActions) return map;
  Object.values(featureActions).forEach((features) => {
    Object.values(features).forEach((actions) => {
      actions.forEach((item) => {
        const [actionId, actionName] = Object.entries(item)[0];
        map.set(actionId, actionName);
      });
    });
  });
  return map;
}

export function formatDateTime(isoString: string | undefined | null): string {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const yy = String(date.getFullYear());
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const HH = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${yy}-${MM}-${dd} ${HH}:${mm}`;
  } catch {
    return "—";
  }
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@(amazon\.com|lab126\.com)$/i.test(value);
}

export function isValidVendorEmail(value: string): boolean {
  return /^[^\s@]+@lassostg\.labcollab\.net$/.test(value);
}

export function transformAssignmentsToOclView(assignmentsData: PermissionAssignmentsData | null | undefined): OclViewItem[] {
  if (!assignmentsData?.assignments) return [];
  const oclMap = new Map<string, OclViewItem>();
  assignmentsData.assignments.forEach((a: PermissionAssignment) => {
    a.operations.forEach((op) => {
      a.cm_locations.forEach((cl) => {
        const locs = cl.location.length > 0 ? cl.location : ["All"];
        locs.forEach((loc) => {
          const key = `${a.system}|${op}|${cl.cm}|${loc}`;
          if (!oclMap.has(key)) {
            oclMap.set(key, {
              id: key,
              system: a.system as OclViewItem["system"],
              operation: op,
              cm: cl.cm,
              location: loc,
              users: [],
              status: "inactive",
            });
          }
          const entry = oclMap.get(key)!;
          const existingUser = entry.users.find((u) => u.user === a.entity && u.assignmentId === a.assignment_id);
          if (existingUser) {
            a.roles.forEach((role) => {
              if (!existingUser.roles.includes(role)) {
                existingUser.roles.push(role);
              }
            });
          } else {
            entry.users.push({
              user: a.entity,
              entityType: a.entity_type,
              assignmentId: a.assignment_id,
              roles: a.roles.length > 0 ? [...a.roles] : [],
              programs: a.programs,
              createdBy: a.created_by,
              status: a.status,
              version: a.version || 1,
            });
          }
          if (a.status === "active") {
            entry.status = "active";
          }
        });
      });
    });
  });
  return Array.from(oclMap.values());
}

export const handleMultiselectAll = (
  selected: readonly any[],
  prev: readonly any[],
  setter: (val: readonly any[]) => void,
  allOptions: readonly any[] = []
) => {
  const hasAll = selected.some((o) => o.value === "All");
  const prevHadAll = prev.some((o: any) => o.value === "All");

  if (hasAll && !prevHadAll) {
    const allLeafOptions: any[] = [ALL_OPTION];
    allOptions.forEach((opt) => {
      if (opt.options) {
        opt.options.forEach((child: any) => { if (!child.disabled) allLeafOptions.push(child); });
      } else if (opt.value !== "All" && !opt.disabled) {
        allLeafOptions.push(opt);
      }
    });
    setter(allLeafOptions);
  } else if (prevHadAll && !hasAll) {
    setter([]);
  } else if (hasAll && selected.length > 1) {
    setter(selected.filter((o) => o.value !== "All"));
  } else {
    // Check if all enabled leaf options are now selected — if so, auto-check "All"
    const enabledLeafValues: string[] = [];
    allOptions.forEach((opt) => {
      if (opt.options) {
        opt.options.forEach((child: any) => { if (!child.disabled) enabledLeafValues.push(child.value); });
      } else if (opt.value !== "All" && !opt.disabled) {
        enabledLeafValues.push(opt.value);
      }
    });
    const selectedValues = new Set(selected.map((o) => o.value));
    if (enabledLeafValues.length > 0 && enabledLeafValues.every((v) => selectedValues.has(v))) {
      setter([ALL_OPTION, ...selected]);
    } else {
      setter(selected);
    }
  }
};
